import { Alert, Button, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconSearch } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { odpriPdf } from '../api/pdf';
import { imeOsebe, naslov } from '../osebe/vrste';
import { IzbiraOsebe } from './IzbiraOsebe';
import { PomocIkona } from '../pomoc/PomocIkona';
import { nazivVozila } from './vozilo';

type Vozilo = Shema['VoziloPodatki'];
type Oseba = Shema['OsebaPodatki'];

const opisVozila = (v: Vozilo) => [nazivVozila(v), v.vin].filter(Boolean).join(' · ');

/**
 * Izpis dovoljenja za preizkušnjo (preizkusne tablice): vozilo in uporabnik
 * vozila (eden je lahko že izbran: iz podrobnosti vozila ali uporabnika vozila), registrska oznaka in dneva veljave.
 * Privzete vrednosti, preverjanje in shranjevanje so na strežniku; nato se odpre PDF.
 */
export function IzpisPreizkusnje({
  odprto,
  zapri,
  vozilo,
  uporabnik,
}: {
  odprto: boolean;
  zapri: () => void;
  vozilo?: Vozilo;
  uporabnik?: Oseba;
}) {
  return (
    <Modal
      opened={odprto}
      onClose={zapri}
      title={
        <Group gap={4} wrap="nowrap">
          Izpis preizkušnje
          <PomocIkona clanek="preizkusnja" />
        </Group>
      }
      radius="lg"
      size="lg"
    >
      {odprto && <Obrazec zapri={zapri} vozilo={vozilo} uporabnik={uporabnik} />}
    </Modal>
  );
}

function Obrazec({ zapri, vozilo, uporabnik }: { zapri: () => void; vozilo?: Vozilo; uporabnik?: Oseba }) {
  const privzeto = useQuery({
    queryKey: ['preizkusnja-nova'],
    queryFn: ({ signal }) => podatki(api.GET('/api/preizkusnje/nova', { signal })),
    gcTime: 0,
  });
  const [izbranoVozilo, setVozilo] = useState<Vozilo | null>(vozilo ?? null);
  const [izbranUporabnik, setUporabnik] = useState<Oseba | null>(uporabnik ?? null);
  const [vnos, setVnos] = useState<{ regStevilka?: string; zacetek?: string | null; konec?: string | null }>({});
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const p = privzeto.data;
  const reg = vnos.regStevilka ?? p?.regStevilka ?? '';
  const zacetek = vnos.zacetek !== undefined ? vnos.zacetek : (p?.zacetek ?? null);
  const konec = vnos.konec !== undefined ? vnos.konec : (p?.konec ?? null);

  const izpisi = async () => {
    setNapaka(null);
    setPolja({});
    setDela(true);
    const izid: { shranjena?: Shema['PreizkusnjaPodatki'] } = {};
    try {
      // zavihek za PDF se odpre ob kliku; preizkušnja se shrani in nato izpiše
      await odpriPdf(
        (async () => {
          izid.shranjena = await podatki(
            api.POST('/api/preizkusnje/izpis', {
              body: {
                voziloId: izbranoVozilo?.id,
                uporabnikVozilaId: izbranUporabnik?.id,
                regStevilka: reg,
                zacetek: zacetek ?? undefined,
                konec: konec ?? undefined,
              },
            }),
          );
          return api.GET('/api/preizkusnje/{id}/pdf', {
            params: { path: { id: izid.shranjena.id! } },
            parseAs: 'blob',
          });
        })(),
      );
      notifications.show({
        color: 'green',
        message: `Preizkušnja ${izid.shranjena?.stPreizkusnje ?? ''} je izpisana.`,
      });
      zapri();
    } catch (e) {
      if (izid.shranjena) {
        notifications.show({
          color: 'red',
          message: `Preizkušnja ${izid.shranjena.stPreizkusnje} je shranjena, PDF pa ni bil odprt: ${sporocilo(e)}`,
        });
        zapri();
      } else if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Stack gap="sm">
      {vozilo ? (
        <Izbrano oznaka="Vozilo" besedilo={opisVozila(vozilo)} />
      ) : (
        <IzbiraVozila izbrano={izbranoVozilo} izberi={setVozilo} napaka={polja.voziloId} />
      )}
      {uporabnik ? (
        <Izbrano
          oznaka="Uporabnik vozila"
          besedilo={[imeOsebe(uporabnik), naslov(uporabnik)].filter(Boolean).join(', ')}
        />
      ) : (
        <IzbiraOsebe
          vrsta="uporabniki-vozil"
          oznaka="Uporabnik vozila"
          izbran={izbranUporabnik}
          izberi={setUporabnik}
          napaka={polja.uporabnikVozilaId}
        />
      )}
      <TextInput
        label="Registrska oznaka"
        placeholder="npr. LJ P1-234"
        value={reg}
        onChange={(e) => setVnos({ ...vnos, regStevilka: e.currentTarget.value })}
        error={polja.regStevilka}
        withAsterisk
      />
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
        <DatePickerInput
          label="Začetek veljave"
          value={zacetek}
          onChange={(v) => setVnos({ ...vnos, zacetek: v })}
          valueFormat="D. M. YYYY"
          error={polja.zacetek}
        />
        <DatePickerInput
          label="Konec veljave"
          value={konec}
          onChange={(v) => setVnos({ ...vnos, konec: v })}
          valueFormat="D. M. YYYY"
          error={polja.konec}
        />
      </SimpleGrid>
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={dela}>
          Prekliči
        </Button>
        <Button onClick={() => void izpisi()} loading={dela || privzeto.isPending}>
          Izpiši
        </Button>
      </Group>
    </Stack>
  );
}

function Izbrano({ oznaka, besedilo }: { oznaka: string; besedilo: string }) {
  return (
    <div>
      <Text size="sm" fw={500}>
        {oznaka}
      </Text>
      <Text size="sm" c="dimmed">
        {besedilo}
      </Text>
    </div>
  );
}

/** Vozilo na zalogi z iskanjem na strežniku (kot IsciVozilaDialog). */
function IzbiraVozila({
  izbrano,
  izberi,
  napaka,
}: {
  izbrano: Vozilo | null;
  izberi: (v: Vozilo | null) => void;
  napaka?: string;
}) {
  const [iskanje, setIskanje] = useState('');
  const [zakasnjeno] = useDebouncedValue(iskanje, 300);
  const zadetki = useQuery({
    queryKey: ['vozila-iskanje', zakasnjeno],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/vozila/iskanje', { signal, params: { query: { iskanje: zakasnjeno || undefined } } })),
    placeholderData: keepPreviousData,
  });
  const vozila = new Map<string, Vozilo>();
  if (izbrano?.id) {
    vozila.set(String(izbrano.id), izbrano);
  }
  (zadetki.data ?? []).slice(0, 30).forEach((v) => vozila.set(String(v.id), v));
  return (
    <Select
      label="Vozilo"
      placeholder="Znamka, tip ali VIN"
      withAsterisk
      searchable
      clearable
      leftSection={<IconSearch size={16} />}
      data={[...vozila.entries()].map(([value, v]) => ({ value, label: opisVozila(v) }))}
      value={izbrano?.id ? String(izbrano.id) : null}
      onChange={(v) => izberi(v ? (vozila.get(v) ?? null) : null)}
      searchValue={iskanje}
      onSearchChange={setIskanje}
      filter={({ options }) => options}
      nothingFoundMessage={zadetki.isFetching ? 'Iščem …' : 'Ni vozil na zalogi'}
      error={napaka}
    />
  );
}
